import React from "react";
import { toast } from "sonner";
import { Globe, Play, Pause, Maximize2, Minimize2, SkipForward } from "lucide-react";
import EndOfVideoTipPrompt from "./EndOfVideoTipPrompt";
import { useAuth } from "../../context/AuthContext";

// FullscreenButton — small expand affordance pinned top-right of the
// player. iOS Safari WebView doesn't honour the standard Fullscreen
// API on arbitrary divs (it only allows the <video> element itself
// to go fullscreen via the non-standard webkitEnterFullScreen). So
// we try BOTH paths: iOS-only API on the video first, then the
// standard wrapper.requestFullscreen() for desktop + Android.
// Auto-hides during fullscreen so it doesn't double-up with the
// native exit control.
// FullscreenButton — small expand affordance pinned top-right of the
// player. Iter-89 (2026-08-02): switched from native fullscreen APIs
// to a CSS pseudo-fullscreen (fixed inset-0 overlay). Why: iOS only
// allows the raw <video> into native fullscreen (webkitEnterFullScreen),
// which strips ALL custom controls — users couldn't skip to the next
// video without exiting first (Eric's complaint). The pseudo mode keeps
// our tap-to-pause, Next, and Close controls alive on every platform.
const FullscreenButton = ({ isFs, setIsFs }) => {
  if (isFs) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setIsFs(true); }}
      onPointerDown={(e) => e.stopPropagation()}
      className="absolute top-2 right-2 z-10 bg-black/55 hover:bg-black/80 active:bg-black/90 backdrop-blur-sm text-white rounded-full p-2 transition-colors"
      aria-label="Enter fullscreen"
      title="Enter fullscreen"
      data-testid="player-fullscreen-btn"
    >
      <Maximize2 className="h-4 w-4" />
    </button>
  );
};

// Controls shown only while in pseudo-fullscreen: exit (top-right) and
// skip-to-next (bottom-right, thumb reach). Both stop propagation so
// the tap-to-pause layer underneath doesn't also fire.
const FullscreenControls = ({ setIsFs, onSkipNext }) => (
  <>
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setIsFs(false); }}
      onPointerDown={(e) => e.stopPropagation()}
      className="absolute top-3 left-3 z-20 bg-black/60 hover:bg-black/85 backdrop-blur-sm text-white rounded-full p-2.5 transition-colors"
      aria-label="Exit fullscreen"
      data-testid="player-exit-fullscreen-btn"
      style={{ marginTop: "env(safe-area-inset-top, 0px)" }}
    >
      <Minimize2 className="h-5 w-5" />
    </button>
    {onSkipNext && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onSkipNext(); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute left-1/2 -translate-x-1/2 z-20 bg-emerald-500 hover:bg-emerald-400 text-gray-950 rounded-full px-5 py-2.5 flex items-center gap-1.5 text-sm font-bold shadow-lg transition-colors"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)" }}
        aria-label="Next video"
        data-testid="player-fs-next-btn"
      >
        Next <SkipForward className="h-4 w-4" />
      </button>
    )}
  </>
);

// PlayPauseOverlay — invisible tap layer that covers the <video> and
// toggles play/pause on every tap. Renders a big centered Play icon
// when paused, fades out completely while playing. Replaces native
// iOS controls so the gesture is identical on iPhone, iPad, and web.
const PlayPauseOverlay = ({ videoRef, userPausedRef }) => {
  const [, force] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    const handler = () => force();
    window.addEventListener('globaleyes:player-toggled', handler);
    return () => window.removeEventListener('globaleyes:player-toggled', handler);
  }, []);

  const paused = !!videoRef.current?.paused;

  const toggle = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      userPausedRef.current = false;
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      userPausedRef.current = true;
      v.pause();
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className="absolute inset-0 w-full h-full flex items-center justify-center bg-transparent focus:outline-none active:bg-black/10 transition-colors"
      aria-label={paused ? "Play video" : "Pause video"}
      data-testid="player-tap-overlay"
    >
      {paused ? (
        <span className="bg-black/60 backdrop-blur-sm rounded-full p-4 pointer-events-none">
          <Play className="h-12 w-12 text-white fill-white" />
        </span>
      ) : (
        <span className="bg-black/50 rounded-full p-3 opacity-0 pointer-events-none">
          <Pause className="h-10 w-10 text-white fill-white" />
        </span>
      )}
    </button>
  );
};

// PlayerWithFullscreen — wraps the active <video> and overlays both
// the play/pause tap layer and the fullscreen affordance. Split out
// so we can hold a local wrapRef without leaking it through props
// (the parent FeedVideoPlayer doesn't own the wrapper element).
const PlayerWithFullscreen = ({
  v,
  videoRef,
  userPausedRef,
  handleVideoEnded,
  failedIdsRef,
  exhaustedToastShownRef,
  findNextPlayableIndex,
  currentVideoIndex,
  setCurrentVideoIndex,
  authUser,
  handleSkipNext,
}) => {
  const wrapRef = React.useRef(null);
  const [isFs, setIsFs] = React.useState(false);
  // Esc exits pseudo-fullscreen (desktop parity with native FS). The
  // record FAB stays visible in fullscreen (instant production mode is
  // the product promise) — it fires ge-exit-video-fs before opening
  // the recorder so the dialog is never buried under the overlay.
  React.useEffect(() => {
    const exit = () => setIsFs(false);
    window.addEventListener("ge-exit-video-fs", exit);
    return () => window.removeEventListener("ge-exit-video-fs", exit);
  }, []);
  React.useEffect(() => {
    if (!isFs) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setIsFs(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFs]);
  return (
    <div
      ref={wrapRef}
      className={isFs
        ? "fixed inset-0 z-[3000] bg-black"
        : "relative w-full bg-black group"}
      data-testid="player-video-wrap"
      style={isFs ? undefined : {
        // Bulletproof 16:9 lock — Iter-58.4 (2026-06-01) replacing
        // Tailwind's `aspect-video` (CSS `aspect-ratio: 16/9`) which
        // user reported was producing different player heights per
        // video on the live site. The padding-bottom hack is immune
        // to flex sizing interference; the actual <video> is
        // absolutely positioned so it can't push the box taller than
        // 16:9 even if intrinsic dimensions disagree.
        paddingBottom: '56.25%',
        height: 0,
      }}
    >
      <video
        ref={videoRef}
        key={v.id}
        src={v.video_url}
        poster={v.thumbnail_url || undefined}
        className="absolute inset-0 w-full h-full object-contain"
        // NO `controls` attribute — iOS WKWebView's native controls
        // were the source of the "pause doesn't pause" bug. We render
        // our OWN tap-overlay so play/pause is one explicit code path.
        playsInline
        webkit-playsinline="true"
        preload="auto"
        onEnded={handleVideoEnded}
        onPause={() => {
          const vid = videoRef.current;
          if (!vid) return;
          const remaining = (vid.duration || 0) - (vid.currentTime || 0);
          if (remaining > 0.05) {
            userPausedRef.current = true;
          }
          window.dispatchEvent(new CustomEvent('globaleyes:player-toggled'));
        }}
        onPlay={() => {
          userPausedRef.current = false;
          window.dispatchEvent(new CustomEvent('globaleyes:player-toggled'));
        }}
        onError={() => {
          if (!v) return;
          if (userPausedRef.current) {
            console.warn(`[player] Ignored onError while user-paused on ${v.id}`);
            return;
          }
          failedIdsRef.current.add(v.id);
          console.warn(`Video failed to load: ${v.id} (${v.video_url}). Silently skipping.`);
          const nextIdx = findNextPlayableIndex(currentVideoIndex);
          if (nextIdx === null) {
            if (!exhaustedToastShownRef.current) {
              exhaustedToastShownRef.current = true;
              toast.error(
                "We couldn't load any videos right now. Tap Home to refresh.",
                { duration: 6000 }
              );
            }
            return;
          }
          setCurrentVideoIndex(nextIdx);
        }}
      />
      {/* Order matters — fullscreen button must paint ABOVE the tap
          overlay (which spans the entire box) or its click is eaten.
          The button uses `z-10` plus its own pointerdown stopPropagation. */}
      <FullscreenButton isFs={isFs} setIsFs={setIsFs} />
      <PlayPauseOverlay videoRef={videoRef} userPausedRef={userPausedRef} />
      {isFs && <FullscreenControls setIsFs={setIsFs} onSkipNext={handleSkipNext} />}
      <EndOfVideoTipPrompt videoRef={videoRef} video={v} user={authUser} />
    </div>
  );
};

// FeedVideoPlayer — the center column. Owns the <video> element, the
// tap-anywhere play/pause overlay, and the empty-playlist placeholder.
// Pure presentation: all refs + handlers come from FeedPage.
const FeedVideoPlayer = ({
  videoRef,
  userPausedRef,
  activeVideos,
  currentVideoIndex,
  setCurrentVideoIndex,
  source,
  activeRegion,
  failedIdsRef,
  exhaustedToastShownRef,
  findNextPlayableIndex,
  handleVideoEnded,
  handleSkipNext,
}) => {
  const v = activeVideos[currentVideoIndex];
  // Iter-58.9 (2026-06-02) — pull the current user for the
  // EndOfVideoTipPrompt visibility gate (skip showing the prompt on
  // your own videos / when logged out).
  const { user: authUser } = useAuth();

  // Iter-58.5 (2026-06-01) — autoplay-on-mount safety net. FeedPage /
  // ProfilePage own a "kick play on currentVideoIndex change" useEffect
  // that fires `videoRef.current.play()` when isPlaying flips true. But
  // that effect lives in the OWNING PAGE's tree, while the <video>
  // element is mounted by PersistentOverlay in PlayerLayout's tree —
  // a separate render cycle. Result: when a user clicks Watch Now /
  // Play Featured, FeedPage's effect fires BEFORE PlayerLayout has
  // committed the video mount, so `videoRef.current` is null and the
  // play() call silently no-ops. User then has to tap the overlay
  // manually. This effect runs INSIDE the player tree, so by the time
  // it fires the ref is guaranteed populated — closing the race.
  const lastAttemptedSrcRef = React.useRef(null);
  React.useEffect(() => {
    if (!v) return undefined;
    const node = videoRef.current;
    if (!node) return undefined;
    // Don't double-fire for the same src (React StrictMode dev re-runs
    // would otherwise re-call play(), interrupting the play promise).
    if (lastAttemptedSrcRef.current === v.video_url) return undefined;
    lastAttemptedSrcRef.current = v.video_url;
    // canplay can fire BEFORE the effect on cached videos, so we try
    // immediately AND attach a one-shot canplay listener as a fallback
    // (iOS WKWebView occasionally needs the extra nudge for the first
    // gesture-granted autoplay of a session).
    const tryPlay = () => {
      // Re-check userPausedRef on EVERY firing — the user could have
      // tapped pause between mount and the canplay event, in which
      // case we must NOT resume.
      if (userPausedRef.current) return;
      const p = node.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* gesture-locked / interrupted — expected on iOS */ });
      }
    };
    tryPlay();
    node.addEventListener('canplay', tryPlay, { once: true });
    return () => node.removeEventListener('canplay', tryPlay);
  }, [v?.video_url, v?.id, videoRef, userPausedRef, v]);

  return (
    <div className="w-full max-w-2xl rounded-lg shadow-2xl bg-black flex flex-col overflow-hidden">
      {v ? (
        <PlayerWithFullscreen
          v={v}
          videoRef={videoRef}
          userPausedRef={userPausedRef}
          handleVideoEnded={handleVideoEnded}
          failedIdsRef={failedIdsRef}
          exhaustedToastShownRef={exhaustedToastShownRef}
          findNextPlayableIndex={findNextPlayableIndex}
          currentVideoIndex={currentVideoIndex}
          setCurrentVideoIndex={setCurrentVideoIndex}
          authUser={authUser}
          handleSkipNext={handleSkipNext}
        />
      ) : (
        <div
          className="relative w-full bg-gradient-to-br from-gray-900 via-black to-gray-900"
          data-testid="player-empty-placeholder"
          style={{
            // Match the active-video wrapper's bulletproof 16:9 lock so
            // the empty/loading state has the EXACT same footprint as a
            // real video — no layout jump on the first / last frame
            // transition (e.g. when a region runs dry).
            paddingBottom: '56.25%',
            height: 0,
          }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <Globe className="h-12 w-12 text-gray-600 mb-3" />
            <p className="text-white font-semibold text-lg mb-1">
              {source === "following"
                ? "Your Following feed is empty"
                : (activeRegion === 'Global' ? "No videos to play yet" : `No videos in ${activeRegion}`)}
            </p>
            <p className="text-gray-400 text-sm max-w-md">
              {source === "following"
                ? "Switch to Global below to find creators to follow, or visit the Map to discover videos near you."
                : (activeRegion === 'Global' ? "Be the first to share your view!" : "Try a different region from the tabs above.")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedVideoPlayer;
